import React, { useState, useEffect } from 'react';
import { trpc } from '@/lib/trpc';
import { Exercise, ExerciseCategory } from '@/lib/types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  ForgeSectionHeader,
  ForgePill,
  ForgeFilterPillRow,
  ForgeListRow,
  ForgeColor,
  forgeColor,
} from '@/components/ui/ForgeUI';

interface ExerciseLibraryProps {
  onSelectExercise?: (exercise: Exercise) => void;
  onRemoveExercise?: (exerciseId: string) => void;
  showSelectButton?: boolean;
  defaultCategory?: string;
  /** IDs of exercises already added to the current workout — shows × icon on those rows */
  addedExerciseIds?: string[];
}

const CATEGORIES: ExerciseCategory[] = ['Chest', 'Back', 'Legs', 'Shoulders', 'Arms', 'Core', 'Cardio', 'Other'];

// Muscle sub-groups per category — simple, clean labels
const MUSCLE_GROUPS: Partial<Record<ExerciseCategory, string[]>> = {
  Back:      ['Upper Back', 'Lats', 'Lower Back', 'Traps'],
  Chest:     ['Upper Chest', 'Mid Chest', 'Lower Chest'],
  Shoulders: ['Front Delts', 'Side Delts', 'Rear Delts'],
  Legs:      ['Quads', 'Hamstrings', 'Glutes', 'Calves'],
  Arms:      ['Biceps', 'Triceps', 'Forearms'],
  Core:      ['Abs', 'Obliques', 'Lower Back'],
};

/** Map ExerciseCategory to a ForgeColor for accent borders and pills */
function categoryToForgeColor(cat: ExerciseCategory | 'All'): ForgeColor {
  switch (cat) {
    case 'Legs':      return 'legs';
    case 'Chest':     return 'chest';
    case 'Back':      return 'pull';
    case 'Shoulders': return 'arms-shoulders';
    case 'Arms':      return 'arms-shoulders';
    case 'Core':      return 'core';
    case 'Cardio':    return 'teal';
    default:          return 'grey';
  }
}

export default function ExerciseLibrary({
  onSelectExercise,
  onRemoveExercise,
  showSelectButton = false,
  defaultCategory,
  addedExerciseIds = [],
}: ExerciseLibraryProps) {
  const [search, setSearch] = useState('');
  const [isAdding, setIsLiveAdding] = useState(false);
  const [newExerciseName, setNewExerciseName] = useState('');
  const [newExerciseCategory, setNewExerciseCategory] = useState<ExerciseCategory>('Chest');
  const [newTrackingMode, setNewTrackingMode] = useState<'strength' | 'time'>('strength');
  const [newSpecificMuscle, setNewSpecificMuscle] = useState('');
  const [selectedMuscle, setSelectedMuscle] = useState<string | null>(null);
  /** ID of the exercise row currently flashing a tap confirmation */
  const [flashedId, setFlashedId] = useState<string | null>(null);

  const utils = trpc.useUtils();
  const { data: rawExercises = [] } = trpc.exercises.list.useQuery();

  // Normalize null fields to undefined for type compatibility
  const exercises: Exercise[] = rawExercises.map((e) => ({
    id: e.id,
    name: e.name,
    category: e.category,
    specificMuscle: (e as any).specificMuscle ?? undefined,
    notes: e.notes ?? undefined,
    isCustom: e.isCustom,
    trackingMode: e.trackingMode,
    defaultTimerSeconds: e.defaultTimerSeconds ?? undefined,
  }));

  const createMutation = trpc.exercises.create.useMutation({
    onSuccess: () => {
      utils.exercises.list.invalidate();
    },
  });

  const deleteMutation = trpc.exercises.delete.useMutation({
    onSuccess: () => {
      utils.exercises.list.invalidate();
    },
  });

  const getMappedCategory = (): ExerciseCategory | 'All' => {
    if (!defaultCategory) return 'All';
    const cat = defaultCategory.toLowerCase();
    if (cat.includes('push')) return 'Chest';
    if (cat.includes('pull')) return 'Back';
    if (cat.includes('legs') || cat.includes('lower')) return 'Legs';
    if (cat.includes('shoulders')) return 'Shoulders';
    if (cat.includes('arms')) return 'Arms';
    if (cat.includes('core')) return 'Core';
    if (cat.includes('cardio')) return 'Cardio';
    if (cat.includes('chest')) return 'Chest';
    if (cat.includes('back')) return 'Back';
    if (cat.includes('upper')) return 'Chest';
    return 'All';
  };

  const [selectedCategory, setSelectedCategory] = useState<ExerciseCategory | 'All'>(() => getMappedCategory());

  useEffect(() => {
    setSelectedCategory(getMappedCategory());
    setSelectedMuscle(null);
  }, [defaultCategory]);

  // Reset muscle sub-filter when category changes
  const handleCategoryChange = (val: string) => {
    setSelectedCategory(val as ExerciseCategory | 'All');
    setSelectedMuscle(null);
  };

  const handleAddExerciseForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newExerciseName.trim()) {
      toast.error('Please enter an exercise name');
      return;
    }
    await createMutation.mutateAsync({
      name: newExerciseName.trim(),
      category: newExerciseCategory,
      trackingMode: newTrackingMode,
      specificMuscle: newSpecificMuscle.trim() || undefined,
    });
    toast.success(`Added "${newExerciseName}" to library`);
    setNewExerciseName('');
    setNewSpecificMuscle('');
    setIsLiveAdding(false);
  };

  const handleDeleteFromVault = async (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete "${name}"?`)) {
      await deleteMutation.mutateAsync({ exerciseId: id });
      toast.success(`Deleted "${name}"`);
    }
  };

  /** Tap a row to add the exercise — shows a 200ms flash confirmation */
  const handleRowTap = (exercise: Exercise) => {
    if (!onSelectExercise) return;
    onSelectExercise(exercise);
    // Flash the row accent border for 200ms
    setFlashedId(exercise.id);
    setTimeout(() => setFlashedId(null), 200);
  };

  const filteredExercises = exercises.filter((exercise) => {
    const matchesSearch = exercise.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || exercise.category === selectedCategory;
    const matchesMuscle = !selectedMuscle || exercise.specificMuscle === selectedMuscle;
    return matchesSearch && matchesCategory && matchesMuscle;
  });

  // Sub-groups available for the currently selected category
  const subGroups = selectedCategory !== 'All' ? (MUSCLE_GROUPS[selectedCategory as ExerciseCategory] ?? null) : null;

  // Build filter pill options
  const categoryOptions = [
    { key: 'All', label: 'ALL' },
    ...CATEGORIES.map((cat) => ({ key: cat, label: cat.toUpperCase() })),
  ];

  const subGroupOptions = subGroups
    ? [
        { key: '__all__', label: `ALL ${selectedCategory.toUpperCase()}` },
        ...subGroups.map((m) => ({ key: m, label: m.toUpperCase() })),
      ]
    : [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <ForgeSectionHeader
        label="Exercise Vault"
        count={filteredExercises.length}
        countLabel="LOADED"
        noPlural
      />

      {/* Search + NEW LIFT */}
      <div className="flex flex-col md:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search exercises..."
            className="pl-8 bg-secondary border-border"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button
          variant="ghost"
          onClick={() => setIsLiveAdding(!isAdding)}
          className="bg-white border-[1.5px] border-[#1B4332] text-[#1B4332] hover:bg-[#1B4332]/5 font-mono text-xs rounded-lg"
        >
          <Plus className="h-4 w-4 mr-1" /> NEW LIFT
        </Button>
      </div>

      {/* New lift form */}
      {isAdding && (
        <form onSubmit={handleAddExerciseForm} className="p-4 bg-secondary border border-primary/30 rounded-sm space-y-3">
          <div className="text-xs font-mono text-primary uppercase tracking-wider">Configure Custom Lift</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-1 font-mono uppercase">Lift Name</label>
              <Input
                placeholder="e.g. Incline DB Press"
                value={newExerciseName}
                onChange={(e) => setNewExerciseName(e.target.value)}
                className="bg-background border-border"
              />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1 font-mono uppercase">Category</label>
              <select
                value={newExerciseCategory}
                onChange={(e) => {
                  const cat = e.target.value as ExerciseCategory;
                  setNewExerciseCategory(cat);
                  setNewSpecificMuscle('');
                  if (cat === 'Cardio' || cat === 'Core') {
                    setNewTrackingMode('time');
                  } else {
                    setNewTrackingMode('strength');
                  }
                }}
                className="w-full h-9 rounded-sm border border-border bg-background text-foreground px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Specific Muscle sub-group selector */}
            {MUSCLE_GROUPS[newExerciseCategory] && (
              <div>
                <label className="block text-xs text-muted-foreground mb-1 font-mono uppercase">Specific Muscle</label>
                <select
                  value={newSpecificMuscle}
                  onChange={(e) => setNewSpecificMuscle(e.target.value)}
                  className="w-full h-9 rounded-sm border border-border bg-background text-foreground px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="">— General —</option>
                  {MUSCLE_GROUPS[newExerciseCategory]!.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            )}

            <div className={MUSCLE_GROUPS[newExerciseCategory] ? '' : 'sm:col-span-2'}>
              <label className="block text-xs text-muted-foreground mb-1 font-mono uppercase">Tracking Mode</label>
              <select
                value={newTrackingMode}
                onChange={(e) => setNewTrackingMode(e.target.value as 'strength' | 'time')}
                className="w-full h-9 rounded-sm border border-border bg-background text-foreground px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="strength">Strength (Sets, Reps, Weight)</option>
                <option value="time">Time-Based (Sets, Duration Timer, Weight)</option>
              </select>
              <p className="text-[10px] text-muted-foreground font-mono mt-1">
                Choose Strength for weightlifting, or Time-Based for core/cardio holds, planks, or runs.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setIsLiveAdding(false)} className="font-mono text-xs">
              CANCEL
            </Button>
            <Button type="submit" size="sm" disabled={createMutation.isPending} className="bg-primary text-primary-foreground font-mono text-xs">
              SAVE TO VAULT
            </Button>
          </div>
        </form>
      )}

      {/* Category filter pill row — no grey container box */}
      <ForgeFilterPillRow
        options={categoryOptions}
        value={selectedCategory}
        onChange={handleCategoryChange}
      />

      {/* Sub-filter pill row — active = solid category color */}
      {subGroups && (
        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none">
          {subGroupOptions.map((opt) => {
            const isActive = opt.key === '__all__' ? selectedMuscle === null : selectedMuscle === opt.key;
            const catColor = categoryToForgeColor(selectedCategory as ExerciseCategory);
            return (
              <button
                key={opt.key}
                onClick={() => setSelectedMuscle(opt.key === '__all__' ? null : (selectedMuscle === opt.key ? null : opt.key))}
                className="shrink-0"
              >
                <ForgePill
                  color={isActive ? catColor : 'grey'}
                  label={opt.label}
                  solid={isActive}
                  size="xs"
                />
              </button>
            );
          })}
        </div>
      )}

      {/* Exercise list */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 max-h-[520px] overflow-y-auto pr-1">
        {filteredExercises.length === 0 ? (
          <div className="col-span-full py-8 text-center text-muted-foreground font-mono text-sm">
            NO LIFTS FOUND MATCHING CRITERIA
          </div>
        ) : (
          filteredExercises.map((exercise) => {
            const catColor = categoryToForgeColor(exercise.category as ExerciseCategory | 'All');
            const isAdded = addedExerciseIds.includes(exercise.id);
            const isFlashing = flashedId === exercise.id;
            // When flashing, boost the accent to full opacity for a brief confirmation
            const accentCss = isFlashing
              ? forgeColor(catColor).cssVar
              : undefined;

            return (
              <ForgeListRow
                key={exercise.id}
                color={catColor}
                accentCss={accentCss}
                className={isFlashing ? 'ring-1 ring-inset transition-all duration-200' : ''}
                title={
                  <span className="flex items-center gap-1.5">
                    {exercise.name}
                    {exercise.trackingMode === 'time' && (
                      <ForgePill color="teal" label="TIMED" size="xs" />
                    )}
                  </span>
                }
                subtitle={
                  <span className="flex items-center gap-1.5">
                    <ForgePill color={catColor} label={exercise.category} size="xs" />
                    {exercise.specificMuscle && (
                      <span className="text-muted-foreground/60">{exercise.specificMuscle}</span>
                    )}
                  </span>
                }
                onClick={onSelectExercise ? () => handleRowTap(exercise) : undefined}
                action={
                  isAdded && onRemoveExercise ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveExercise(exercise.id);
                      }}
                      className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                      aria-label={`Remove ${exercise.name}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  ) : isAdded ? (
                    // Added but no remove handler (e.g. swap modal) — show nothing
                    undefined
                  ) : exercise.isCustom && !onSelectExercise ? (
                    // Standalone vault view (no select mode) — show delete icon for custom exercises
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFromVault(exercise.id, exercise.name);
                      }}
                      className="p-1 text-destructive/50 hover:text-destructive transition-colors"
                      aria-label={`Delete ${exercise.name}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  ) : undefined
                }
              />
            );
          })
        )}
      </div>
    </div>
  );
}
